import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowUp, FileSpreadsheet, MessageSquareText, FileSearch,
  CheckCheck, PenLine, ShieldCheck, ChevronDown,
} from "lucide-react";
import { fmtRel } from "./widgets";

const MODEL_LABELS = { claude: "Claude", gemini: "Gemini" };
const MODEL_DOTS = { claude: "#D97757", gemini: "#4E86F5" };

const TOOLS = [
  { label: "Riconcilia file", to: "/assistant/reconcile", icon: FileSpreadsheet, testId: "ai-tool-reconcile" },
  { label: "Chiedi al file", to: "/assistant/ask-file", icon: MessageSquareText, testId: "ai-tool-askfile" },
  { label: "Analizza documento", to: "/assistant/analyze-doc", icon: FileSearch, testId: "ai-tool-analyzedoc" },
  { label: "Controlli qualità", to: "/assistant/quality", icon: CheckCheck, testId: "ai-tool-quality" },
  { label: "Scrivi una bozza", to: "/assistant/drafts", icon: PenLine, testId: "ai-tool-drafts" },
];

const ModelPicker = ({ models, model, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => models.length > 1 && setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-xs text-white/85 bg-white/[0.08] hover:bg-white/[0.14] border border-white/15 rounded-lg px-2.5 py-2 transition-colors"
              aria-haspopup="listbox" aria-expanded={open}
              data-testid="dashboard-ai-model-picker">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: MODEL_DOTS[model] || "#F4F430" }} aria-hidden="true" />
        {MODEL_LABELS[model] || model}
        {models.length > 1 && <ChevronDown size={12} aria-hidden="true" className="text-white/50" />}
      </button>
      {open && (
        <div className="absolute bottom-full right-0 mb-1.5 min-w-[130px] rounded-lg border border-white/15 py-1 z-20"
             style={{ background: "#232329" }} role="listbox">
          {models.map((m) => (
            <button key={m} type="button" role="option" aria-selected={m === model}
                    onClick={() => { onChange(m); setOpen(false); }}
                    className={`w-full flex items-center gap-2 text-left text-xs px-3 py-2 hover:bg-white/[0.08] ${m === model ? "text-white font-medium" : "text-white/60"}`}
                    data-testid={`dashboard-ai-model-${m}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: MODEL_DOTS[m] || "#F4F430" }} aria-hidden="true" />
              {MODEL_LABELS[m] || m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AiPanel = ({ ai, isAdmin }) => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const models = ai?.models?.length ? ai.models : ["claude"];
  const [model, setModel] = useState(
    ai?.default_model && models.includes(ai.default_model) ? ai.default_model : models[0]);

  const go = () => {
    const t = text.trim();
    if (!t) return;
    navigate("/assistant", { state: { initialMessage: t, model } });
  };

  const modelNames = models.map((m) => MODEL_LABELS[m] || m).join(", ");

  return (
    <section className="relative overflow-hidden rounded-[10px] p-5 md:p-6 h-full flex flex-col text-white"
             style={{ background: "#16161A" }} data-testid="dashboard-ai-panel">
      <div aria-hidden="true" className="absolute -top-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
           style={{ background: "radial-gradient(closest-side, rgba(244,244,48,0.28), transparent)" }} />

      <div className="flex items-center gap-2.5 flex-wrap mb-3 relative">
        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0"
              style={{ background: "#F4F430", color: "#16161A" }} data-testid="ai-panel-badge">
          AI aziendale
        </span>
        <span className="text-xs text-white/50">Tutte le conversazioni restano nello storico dell&apos;azienda</span>
      </div>

      <h2 className="text-xl md:text-2xl font-semibold mb-4 relative"
          style={{ fontFamily: "var(--wl-font-display)" }}>
        Cosa facciamo{" "}
        <span className="px-1.5 rounded" style={{ background: "#F4F430", color: "#16161A" }}>oggi</span>?
      </h2>

      <div className="relative flex items-center gap-2 bg-white/[0.06] border border-white/15 rounded-xl pl-4 pr-1.5 py-1.5 focus-within:border-white/35 transition-colors">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
          placeholder="Es. confronta il file fatture di agosto con l'estratto conto e dimmi cosa non torna…"
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-white/40 focus:outline-none h-9"
          aria-label="Scrivi un messaggio per l'AI aziendale"
          data-testid="dashboard-ai-input"
        />
        <ModelPicker models={models} model={model} onChange={setModel} />
        <button type="button" onClick={go} disabled={!text.trim()}
                className="w-10 h-10 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 transition-opacity disabled:opacity-40"
                style={{ background: "#F4F430", color: "#16161A" }}
                aria-label="Invia e apri la chat" data-testid="dashboard-ai-send">
          <ArrowUp size={16} strokeWidth={2.2} />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-4 relative">
        {TOOLS.map((t) => (
          <Link key={t.to} to={t.to} data-testid={t.testId}
                className="inline-flex items-center gap-1.5 text-xs text-white/75 hover:text-white bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-full px-3 py-2 transition-colors min-h-[36px]">
            <t.icon size={12} strokeWidth={1.8} aria-hidden="true" />
            {t.label}
          </Link>
        ))}
      </div>

      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-auto pt-4 text-[11px] text-white/45 relative"
           data-testid="dashboard-ai-status">
        <span data-testid="ai-models-label">
          {isAdmin ? "Modelli assegnati da te" : "Modelli disponibili per te"}: {modelNames}
        </span>
        {isAdmin && ai?.ops_month && (
          <span data-testid="ai-ops-month">
            {ai.ops_month.used}{ai.ops_month.limit ? ` / ${ai.ops_month.limit}` : ""} operazioni questo mese
          </span>
        )}
        {!isAdmin && (
          <span className="inline-flex items-center gap-1">
            <ShieldCheck size={11} aria-hidden="true" />
            Le tue conversazioni restano visibili all&apos;amministratore
          </span>
        )}
        {ai?.last_conversation && (
          <button type="button"
                  onClick={() => navigate("/assistant", { state: { conversationId: ai.last_conversation.id } })}
                  className="hover:text-white/80 underline decoration-white/20 truncate max-w-[260px]"
                  data-testid="ai-last-conversation">
            Ultima: &ldquo;{ai.last_conversation.title}&rdquo;
            {ai.last_conversation.last_message_at ? ` · ${fmtRel(ai.last_conversation.last_message_at)}` : ""}
          </button>
        )}
      </div>
    </section>
  );
};
